import {GRANITE_1, GRANITE_3} from '@gaia/colors';
import Button from '@gaia/components/Button';
import {useLatest} from '@gaia/hooks/useLatest';
import {recaptchaActionForRegistration, useReCaptcha} from '@gaia/hooks/useRecaptcha';
import {gaiaOutside} from '@gaia/images/logos';
import {useRefreshUser} from '@gaia/stores/user';
import theme from '@gaia/theme';
import type {AuthFormProps} from '@gaia/types/AuthFormProps';
import gettext from '@gaia/utils/django/gettext';
import fetchJSON from '@gaia/utils/fetchJSON';
import getCSRFToken from '@gaia/utils/gaia/getCSRFToken';
import useSyncPromise from '@gaia/utils/react/useSyncPromise';
import {CircularProgress, Link, TextField, Typography} from '@mui/material';
import qs from 'qs';
import {useCallback, useEffect, useState} from 'react';
import {GoogleReCaptcha} from 'react-google-recaptcha-v3';

import LoginFormContainer from './LoginFormContainer';

interface Response {
    code: number;
    message: string;
}

export default function RegistrationForm({
    nextPage,
    setAuthModalType,
    onSubmitDone,
    onUserAuthenticated,
    analyticsContext,
    callToAction,
    hideLoginLink,
}: AuthFormProps) {
    const refreshUser = useRefreshUser();

    const [state, setState] = useState({email: '', password: ''});

    const [responsePromise, setResponsePromise] = useState<Promise<Response> | undefined>();

    const {recaptcha, lastRecaptchaRefresh, triggerRecaptchaRefresh, handleRecaptchaVerify} =
        useReCaptcha();

    const [response, error, isResponseLoaded] = useSyncPromise(responsePromise, undefined);
    const isLoaded = (response ? response.code !== 1000 : isResponseLoaded) && recaptcha !== null;

    const hasInvalidPassword =
        response?.code === 1071 ||
        (response?.code === 1080 && response?.message === 'password invalid');

    const onLoginClick = useCallback(() => {
        if (setAuthModalType) setAuthModalType('login');
        else window.location.href = `/login/?next=${nextPage}`;
    }, [setAuthModalType, nextPage]);

    useEffect(() => {
        if ((response || error) && onSubmitDone) onSubmitDone();
    }, [error, onSubmitDone, response]);

    const latestOnUserAuthenticated = useLatest(onUserAuthenticated);

    useEffect(() => {
        if (response?.code === 1000) {
            refreshUser();
            latestOnUserAuthenticated.current?.();
        }
    }, [latestOnUserAuthenticated, refreshUser, response]);

    function onSubmit(event: React.FormEvent) {
        event.preventDefault();
        if (!isLoaded) return;

        const csrf = getCSRFToken();

        setResponsePromise(
            fetchJSON('/register/addUser/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    ...(csrf && {'X-CSRFToken': csrf}),
                    accept: 'application/json',
                },
                body: qs.stringify({
                    email: state.email,
                    password: state.password,
                    analytics_context: analyticsContext,
                    recaptcha,
                }),
            })
        );
        // Eagerly refresh recaptcha token on submit.
        triggerRecaptchaRefresh();
    }

    return (
        <LoginFormContainer
            panelSecondary={
                <div>
                    <GoogleReCaptcha
                        action={recaptchaActionForRegistration}
                        onVerify={handleRecaptchaVerify}
                        refreshReCaptcha={lastRecaptchaRefresh}
                    />
                    <div style={{marginBottom: theme.spacing(2)}}>
                        <Typography
                            variant='h4'
                            style={{
                                fontSize: 24,
                                fontWeight: 'bold',
                                color: GRANITE_1,
                                display: 'inline',
                            }}
                        >
                            {gettext(callToAction || 'Sign Up')}
                        </Typography>
                        <Typography
                            variant='body1'
                            style={{
                                display: 'inline',
                                marginLeft: theme.spacing(1),
                                color: GRANITE_3,
                            }}
                        >
                            {"It's free!"}
                        </Typography>
                    </div>

                    <form autoComplete='off' autoCapitalize='off' onSubmit={onSubmit}>
                        <TextField
                            id='registration-email'
                            style={{
                                display: 'block',
                                marginBottom: theme.spacing(2),
                            }}
                            fullWidth
                            label={gettext('Email')}
                            type='email'
                            variant='outlined'
                            required
                            value={state.email}
                            onChange={(e) => setState({...state, email: e.target.value})}
                            error={response?.code === 1030 || response?.code === 1070}
                            helperText={
                                (response?.code === 1030 &&
                                    gettext('Email address already in use')) ||
                                (response?.code === 1070 && gettext('Email address invalid'))
                            }
                            InputLabelProps={{
                                required: false,
                                style: {fontSize: 14},
                            }}
                        />
                        <TextField
                            id='registration-password'
                            style={{
                                display: 'block',
                                marginBottom: theme.spacing(2),
                            }}
                            fullWidth
                            label={gettext('Password')}
                            type='password'
                            variant='outlined'
                            required
                            value={state.password}
                            inputProps={{minLength: 8}}
                            onChange={(e) => setState({...state, password: e.target.value})}
                            error={hasInvalidPassword}
                            helperText={
                                hasInvalidPassword &&
                                gettext(
                                    'Passwords must contain a minimum of 8 characters, and at least 1 uppercase letter and 1 number.'
                                )
                            }
                            InputLabelProps={{
                                required: false,
                                style: {fontSize: 14},
                            }}
                        />

                        {(response?.code === 1030 ||
                            (response?.code === 1080 && response?.message === 'email invalid')) && (
                            <Typography
                                variant='body2'
                                style={{
                                    fontSize: 14,
                                    marginBottom: theme.spacing(1),
                                }}
                            >
                                It looks like you may already have an Outside account. Try
                                {` `}
                                <Link onClick={onLoginClick}>logging in</Link>
                                {` `}
                                using your Outside account. If you&apos;ve forgotten your password,
                                {` `}
                                <Link
                                    href='/user/password/reset/?next=/login/'
                                    target='_blank'
                                    rel='noopener'
                                >
                                    reset your password
                                </Link>
                                .
                            </Typography>
                        )}

                        {error && (
                            <Typography
                                variant='body1'
                                color='error'
                                id='unknown-error'
                                style={{
                                    fontSize: 14,
                                    paddingBottom: theme.spacing(2),
                                }}
                            >
                                An unknown error occurred. Please try again.
                            </Typography>
                        )}

                        <Button
                            appearance='primary'
                            type='submit'
                            fullWidth
                            size='large'
                            disabled={!isLoaded}
                            style={{
                                // Since the registration form doesn't have a "Forgot password link" this
                                // marginTop ensures that the spacing and height remains consistent between the
                                // login and registration forms
                                marginTop: error ? 0 : 19,
                            }}
                        >
                            {!isLoaded ? (
                                <CircularProgress thickness={7} size={24} disableShrink />
                            ) : (
                                gettext('Sign Up')
                            )}
                        </Button>
                        {!hideLoginLink && (
                            <Link
                                onClick={onLoginClick}
                                style={{
                                    fontSize: 14,
                                    marginTop: theme.spacing(2),
                                    display: 'block',
                                    width: '100%',
                                    textAlign: 'center',
                                }}
                            >
                                {gettext('Log in with existing account')}
                            </Link>
                        )}
                    </form>
                </div>
            }
            panelPrimary={
                <div style={{textAlign: 'left'}}>
                    <img
                        src={gaiaOutside}
                        alt='Gaia GPS from Outside'
                        style={{marginBottom: theme.spacing(2)}}
                        height='49'
                        width='200'
                    />
                    <Typography
                        variant='body2'
                        style={{fontSize: 14, marginBottom: theme.spacing(2)}}
                    >
                        Gaia GPS is part of the Outside family. Outside members can use their
                        existing email and password to log in to Gaia GPS.
                    </Typography>
                    <Link
                        color='inherit'
                        href='/outsideplus/'
                        style={{fontSize: 14}}
                        target='_blank'
                        rel='noopener noreferrer'
                    >
                        Learn more
                    </Link>
                </div>
            }
        />
    );
}
